import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  IconButton,
  SelectChangeEvent,
} from '@mui/material';
import { ClearIcon } from "../../../../icons";
import {
  CLEAR_FILTER_LABEL,
  ColumnType,
  FilterOperator,
  defaultOperatorByType,
  getOperatorLabels,
  isOperatorAvailable,
  operatorNeedsValue,
} from '../../../services/mrtFilterService';

export interface FilterCellProps {
  columnId: string;
  columnType: ColumnType;
  value: string;
  operator: FilterOperator;
  onChange: (columnId: string, value: string, operator: FilterOperator) => void;
  language?: 'es' | 'en' | 'pt';
  placeholder?: string;
  debounceMs?: number;
  /** La columna admite null (una fecha opcional): solo así se ofrecen "Vacío" y "No vacío". */
  nullable?: boolean;
}

export const FilterCell: React.FC<FilterCellProps> = ({
  columnId,
  columnType,
  value,
  operator,
  onChange,
  language = 'es',
  placeholder,
  debounceMs = 500,
  nullable,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [localOperator, setLocalOperator] = useState<FilterOperator>(operator);

  const clearLabel = CLEAR_FILTER_LABEL[language] ?? CLEAR_FILTER_LABEL.es;
  const operatorLabels = getOperatorLabels(language);
  const operators = (operatorLabels[columnType] || operatorLabels.string).filter((op) =>
    isOperatorAvailable(columnType, op.value, nullable)
  );

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    setLocalOperator(operator);
  }, [operator]);

  // Debounce del valor
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== value || localOperator !== operator) {
        onChange(columnId, localValue, localOperator);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [localValue, localOperator, columnId, onChange, value, operator, debounceMs]);

  const handleValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  const handleOperatorChange = useCallback((e: SelectChangeEvent<FilterOperator>) => {
    const newOperator = e.target.value as FilterOperator;
    // Un operador sin valor (vacío / no vacío) no arrastra el valor que había.
    const newValue = operatorNeedsValue(newOperator) ? localValue : '';
    setLocalOperator(newOperator);
    setLocalValue(newValue);
    // Propagar inmediatamente el cambio de operador
    onChange(columnId, newValue, newOperator);
  }, [columnId, localValue, onChange]);

  const handleClear = useCallback(() => {
    setLocalValue('');
    setLocalOperator(defaultOperatorByType[columnType]);
    onChange(columnId, '', defaultOperatorByType[columnType]);
  }, [columnId, columnType, onChange]);

  const getInputType = (): string => {
    switch (columnType) {
      case 'number':
        return 'number';
      case 'date':
      case 'calendarDate':
        if (localOperator === 'day') return 'date';
        if (localOperator === 'yearMonth') return 'month';
        if (localOperator === 'year') return 'number';
        return 'text'; // fromTo usa texto libre
      default:
        return 'text';
    }
  };

  const getPlaceholder = (): string => {
    if (placeholder) return placeholder;

    switch (columnType) {
      case 'date':
      case 'calendarDate':
        if (localOperator === 'day') return 'YYYY-MM-DD';
        if (localOperator === 'yearMonth') return 'YYYY-MM';
        if (localOperator === 'year') return 'YYYY';
        if (localOperator === 'fromTo') return 'desde, hasta';
        return '';
      case 'boolean':
        return '';
      default:
        return '';
    }
  };

  // Para booleanos, no mostramos input, solo el selector
  if (columnType === 'boolean') {
    return (
      <Box sx={{ display: 'flex', width: '100%' }}>
        <FormControl size="small" fullWidth variant="standard">
          <Select
            value={localOperator}
            onChange={handleOperatorChange}
            size="small"
            variant="standard"
            sx={{
              fontSize: '0.75rem',
              '& .MuiSelect-select': { py: 0.5 },
            }}
          >
            {operators.map((op) => (
              <MenuItem key={op.value} value={op.value} sx={{ fontSize: '0.75rem' }}>
                {op.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, width: '100%', alignItems: 'center' }}>
      {/* Selector de operador */}
      <FormControl size="small" variant="standard" sx={{ minWidth: columnType === 'number' ? 50 : 80 }}>
        <Select
          value={localOperator}
          onChange={handleOperatorChange}
          size="small"
          variant="standard"
          sx={{
            fontSize: '0.7rem',
            '& .MuiSelect-select': { py: 0.5, px: 1 },
          }}
        >
          {operators.map((op) => (
            <MenuItem key={op.value} value={op.value} sx={{ fontSize: '0.75rem' }}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Input de valor. Los operadores sin valor (vacío / no vacío) no lo
          tienen: el filtro se quita con la X. */}
      {operatorNeedsValue(localOperator) ? (
        <TextField
          size="small"
          variant="standard"
          type={getInputType()}
          value={localValue}
          onChange={handleValueChange}
          placeholder={getPlaceholder()}
          /**
           * Sin esto no se puede filtrar por una frase: la barra espaciadora no
           * llega al input.
           *
           * El filtro vive DENTRO de la celda de encabezado, y MRT le pone ahí un
           * manejador de teclado (`enableKeyboardShortcuts`, prendido por
           * defecto). Ante Espacio o Enter, si la columna es ordenable —casi
           * todas— hace `preventDefault()` y reordena. Así que al tipear un
           * espacio pasaban dos cosas: no se escribía, y la grilla se reordenaba
           * sola. Se podía filtrar por una palabra, nunca por dos.
           *
           * Se corta la propagación en vez de apagar `enableKeyboardShortcuts`:
           * apagarlo se lleva puesta la navegación con flechas y el copiado con
           * Ctrl+C de toda la grilla, que no tienen nada que ver. Acá adentro las
           * teclas son del campo de texto y de nadie más.
           */
          onKeyDown={(e) => e.stopPropagation()}
          sx={{
            flex: 1,
            '& .MuiInputBase-input': {
              fontSize: '0.75rem',
              py: 0.5,
            },
          }}
          InputProps={{
            endAdornment: localValue ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClear}
                  sx={{ p: 0.25 }}
                  aria-label={clearLabel}
                >
                  <ClearIcon sx={{ fontSize: '0.875rem' }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      ) : (
        <IconButton
          size="small"
          onClick={handleClear}
          // Fuera del TextField, la X no tiene su manejador de teclado: Enter o
          // Espacio llegarían a la cabecera de MRT, que los toma para reordenar
          // la columna y no deja que el botón se active (ver el TextField).
          onKeyDown={(e) => e.stopPropagation()}
          sx={{ p: 0.25 }}
          aria-label={clearLabel}
        >
          <ClearIcon sx={{ fontSize: '0.875rem' }} />
        </IconButton>
      )}
    </Box>
  );
};

export default FilterCell;
